import { useState } from 'react';
import { UserPlus, UsersRound } from 'lucide-react';
import { SALES_VERTICALS, VERTICAL_LABEL, type SalesRep, type SalesRepRole } from '@/client/types/sales';
import {
  usePatchRepMutation,
  useSalesMeQuery,
  useSalesPolicyQuery,
  useSalesRepsQuery,
} from '@/client/api/sales/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { EmptyState, btnPrimary, toastSales } from './components/sales-ui';
import { RepCard } from './components/team/RepCard';
import { RepFormDialog } from './components/team/RepFormDialog';

const LEVELS: { id: SalesRepRole; label: string }[] = [
  { id: 'manager', label: 'Managers' },
  { id: 'lead', label: 'Leads' },
  { id: 'agent', label: 'Agents' },
];

// `null` = closed, `'new'` = adding, a rep = editing that rep.
type FormState = null | 'new' | SalesRep;

export default function SalesTeamPage() {
  const me = useSalesMeQuery();
  const [showInactive, setShowInactive] = useState(false);
  const repsQ = useSalesRepsQuery(showInactive);
  const policyQ = useSalesPolicyQuery();
  const patchRep = usePatchRepMutation();
  const [form, setForm] = useState<FormState>(null);

  const manage = me.data?.canManageTeam === true;
  const reps = repsQ.data ?? [];
  const onFloor = reps.filter((r) => r.active !== false);
  const cap = policyQ.data?.maxConcurrentPerRep ?? 8;
  const allDesk = onFloor.filter((r) => r.desk === 'all').length;

  return (
    <SalesPageShell
      title="Team"
      description="Who works the floor, at which level and on which desk. Only people on this list can hold a claim."
      crumbs={[{ label: 'Team' }]}
      icon={UsersRound}
      actions={
        manage && (
          <button type="button" className={btnPrimary} onClick={() => setForm('new')}>
            <UserPlus className="h-4 w-4" />
            Add rep
          </button>
        )
      }
      stats={[
        {
          key: 'n',
          label: 'On the floor',
          value: onFloor.length,
          hint: allDesk ? `${allDesk} on all desks` : undefined,
        },
        ...SALES_VERTICALS.map((v) => ({
          key: v,
          label: VERTICAL_LABEL[v],
          value: onFloor.filter((r) => r.desk === v).length,
        })),
      ]}
    >
      <div className="mb-4 flex items-center justify-end">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            className="accent-slate-900"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show people taken off the floor
        </label>
      </div>

      {repsQ.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-36 animate-pulse rounded-xl border border-gray-200 bg-white" />
          ))}
        </div>
      ) : reps.length === 0 ? (
        <EmptyState
          label={manage ? 'No reps yet. Add one before anyone can hold a claim.' : 'No reps on the floor yet.'}
        />
      ) : (
        LEVELS.map((level) => {
          const group = reps.filter((r) => r.role === level.id);
          if (group.length === 0) return null;
          return (
            <section key={level.id} className="mb-6">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                {level.label}
                <span className="rounded-full bg-gray-200 px-1.5 text-[11px] font-medium text-gray-600">
                  {group.length}
                </span>
              </h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {group.map((r) => (
                  <RepCard
                    key={r.id}
                    rep={r}
                    cap={cap}
                    isYou={me.data?.rep?.id === r.id}
                    canManage={manage}
                    onEdit={() => setForm(r)}
                    onToggleActive={(active) =>
                      patchRep.mutate({ id: r.id, active }, { onError: (err) => toastSales(err) })
                    }
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {form && (
        <RepFormDialog
          rep={form === 'new' ? null : form}
          roster={reps}
          onClose={() => setForm(null)}
        />
      )}
    </SalesPageShell>
  );
}
